import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import {
  useFaxProfileQuery,
  useUpdateFaxProfileStatus,
  useUpsertFaxProfile,
} from "hooks/useFaxProfile";
import { useCelleraTheme } from "theme/useCelleraTheme";

import FaxProfileActions from "./FaxProfileActions";
import FaxProfileFormDialog from "./FaxProfileFormDialog";
import FaxStatusBadge from "./FaxStatusBadge";

interface FaxProfileSectionProps {
  practiceId: string;
}

const formatTimestamp = (value: string | Date): string => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const FaxProfileSection: React.FC<FaxProfileSectionProps> = ({
  practiceId,
}) => {
  const { colors } = useCelleraTheme();
  const [dialogOpen, setDialogOpen] = useState(false);

  const query = useFaxProfileQuery(practiceId);
  const upsert = useUpsertFaxProfile(practiceId);
  const updateStatus = useUpdateFaxProfileStatus(practiceId);

  const profile = query.data ?? null;

  const renderCardBody = () => {
    if (query.isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 120,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (query.isError) {
      return (
        <Alert
          severity="error"
          action={
            <Button onClick={() => query.refetch()} size="small">
              Retry
            </Button>
          }
        >
          {query.error?.message ?? "Failed to load fax profile."}
        </Alert>
      );
    }

    if (!profile) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: colors.text.primary }}
            >
              No fax profile configured
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Configure a Sinch fax service for this practice to enable
              receiving and sending faxes.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setDialogOpen(true)}
          >
            Configure Fax Profile
          </Button>
        </Box>
      );
    }

    const canEdit =
      profile.status === "active" || profile.status === "suspended";

    return (
      <>
        {/* Header row — mirrors SubscriptionSummary's title + chip row */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              mr: 2,
              color: colors.primary.main,
              fontWeight: 600,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            }}
          >
            {profile.faxNumber}
          </Typography>
          <FaxStatusBadge status={profile.status} />
          {canEdit && (
            <Button
              variant="outlined"
              size="small"
              sx={{ ml: "auto" }}
              disabled={upsert.isPending}
              onClick={() => setDialogOpen(true)}
            >
              Edit
            </Button>
          )}
        </Box>

        <Divider />

        {/* Actions */}
        <Box sx={{ pt: 2 }}>
          <FaxProfileActions
            status={profile.status}
            loading={updateStatus.isPending}
            onChangeStatus={(status) => updateStatus.mutate(status)}
          />
        </Box>

        {/* Caption */}
        <Typography
          variant="caption"
          sx={{ color: colors.text.secondary, display: "block", mt: 1 }}
        >
          Last updated: {formatTimestamp(profile.updatedAt)}
        </Typography>
      </>
    );
  };

  return (
    <Box>
      <Typography
        variant="h6"
        component="h3"
        sx={{ fontWeight: 600, color: colors.text.primary, mb: 2 }}
      >
        Fax Profile
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>{renderCardBody()}</CardContent>
      </Card>

      <FaxProfileFormDialog
        open={dialogOpen}
        mode={profile ? "edit" : "create"}
        initialSinchServiceId={profile?.sinchServiceId}
        initialFaxNumber={profile?.faxNumber}
        loading={upsert.isPending}
        onClose={() => {
          if (!upsert.isPending) setDialogOpen(false);
        }}
        onSubmit={(data) =>
          upsert.mutate(data, {
            onSuccess: () => setDialogOpen(false),
          })
        }
      />
    </Box>
  );
};

export default FaxProfileSection;
